/**
 * Knowledge Hub API Service
 * Segue o mesmo padrão de api.js existente.
 */

const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

class KbApiService {

  // ═══════════════════════════════════════════════
  // CHAT INTERNO
  // ═══════════════════════════════════════════════

  static async getInternalMessages(sellerId, peerId = 'all', limit = 50, before = null) {
    const params = new URLSearchParams({ seller_id: sellerId, peer_id: peerId, limit });
    if (before) params.append('before', before);

    const res = await fetch(`${API_URL}/kb/internal/messages?${params}`);
    if (!res.ok) throw new Error('Erro ao buscar mensagens internas');
    return res.json();
  }

  static async sendInternalMessage(fromSellerId, toSellerId, content) {
    const res = await fetch(`${API_URL}/kb/internal/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        from_seller_id: fromSellerId,
        to_seller_id: toSellerId,
        content,
      }),
    });
    if (!res.ok) throw new Error('Erro ao enviar mensagem interna');
    return res.json();
  }

  static async markInternalRead(sellerId, peerId) {
    const res = await fetch(`${API_URL}/kb/internal/messages/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seller_id: sellerId, peer_id: peerId }),
    });
    return res.json();
  }

  static async getUnreadCounts(sellerId) {
    const res = await fetch(`${API_URL}/kb/internal/messages/unread?seller_id=${sellerId}`);
    if (!res.ok) return {};
    return res.json();
  }

  // ═══════════════════════════════════════════════
  // PROJETOS
  // ═══════════════════════════════════════════════

  static async getProjects(status = null, assignedTo = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (assignedTo) params.append('assigned_to', assignedTo);

    const res = await fetch(`${API_URL}/kb/projects?${params}`);
    if (!res.ok) throw new Error('Erro ao buscar projetos');
    return res.json();
  }

  static async getProject(projectId) {
    const res = await fetch(`${API_URL}/kb/projects/${projectId}`);
    if (!res.ok) throw new Error('Erro ao buscar projeto');
    return res.json();
  }

  static async createProject(data) {
    const res = await fetch(`${API_URL}/kb/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao criar projeto');
    return res.json();
  }

  static async updateProject(projectId, data) {
    const res = await fetch(`${API_URL}/kb/projects/${projectId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao atualizar projeto');
    return res.json();
  }

  static async deleteProject(projectId) {
    const res = await fetch(`${API_URL}/kb/projects/${projectId}`, { method: 'DELETE' });
    return res.json();
  }

  // ── Steps ──────────────────────────────────────

  static async addStep(projectId, data) {
    const res = await fetch(`${API_URL}/kb/projects/${projectId}/steps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao adicionar etapa');
    return res.json();
  }

  static async updateStep(stepId, data) {
    const res = await fetch(`${API_URL}/kb/projects/steps/${stepId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao atualizar etapa');
    return res.json();
  }

  static async deleteStep(stepId) {
    const res = await fetch(`${API_URL}/kb/projects/steps/${stepId}`, { method: 'DELETE' });
    return res.json();
  }

  // ── Comments ───────────────────────────────────

  static async addComment(projectId, authorId, content) {
    const res = await fetch(`${API_URL}/kb/projects/${projectId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ author_id: authorId, content }),
    });
    if (!res.ok) throw new Error('Erro ao adicionar comentário');
    return res.json();
  }

  static async deleteComment(commentId) {
    const res = await fetch(`${API_URL}/kb/projects/comments/${commentId}`, { method: 'DELETE' });
    return res.json();
  }

  // ═══════════════════════════════════════════════
  // ARTIGOS (BASE OPERACIONAL)
  // ═══════════════════════════════════════════════

  static async getArticles(category = null) {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    const res = await fetch(`${API_URL}/kb/articles${params}`);
    if (!res.ok) throw new Error('Erro ao buscar artigos');
    return res.json();
  }

  static async getArticle(articleId) {
    const res = await fetch(`${API_URL}/kb/articles/${articleId}`);
    if (!res.ok) throw new Error('Erro ao buscar artigo');
    return res.json();
  }

  static async searchArticles(query) {
    const res = await fetch(`${API_URL}/kb/articles/search?q=${encodeURIComponent(query)}`);
    if (!res.ok) throw new Error('Erro na busca');
    return res.json();
  }

  static async getCategories() {
    const res = await fetch(`${API_URL}/kb/articles/categories`);
    if (!res.ok) return [];
    return res.json();
  }

  static async createArticle(data) {
    const res = await fetch(`${API_URL}/kb/articles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao criar artigo');
    return res.json();
  }

  static async updateArticle(articleId, data) {
    const res = await fetch(`${API_URL}/kb/articles/${articleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao atualizar artigo');
    return res.json();
  }

  static async deleteArticle(articleId) {
    const res = await fetch(`${API_URL}/kb/articles/${articleId}`, { method: 'DELETE' });
    return res.json();
  }
}

export default KbApiService;
